"use client";
import React from "react";
import { customSanitize } from "../../../utils/customSanitize";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { CTAListModuleOptionsType } from "@/types/ModuleOptionsType";
import styles from "./CTAListBox.module.scss";
import { getItemAttributes } from "@/components/_primitives/utils/getItemAttributes";
import { Breakpoint } from "@aleksundshantu/aunds-utils-react/layout";
import { Button } from "@/components/Button/Button";
import { CTAListModuleType } from "../CTAList.client";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG.client";
import {
  getBoxPadding,
  replaceExtendedLinkText,
  checkLinkAvailable,
  getShowBoxClasses,
} from "@/utils/infoBoxUtils";
import Image from "next/image";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { getIconSize } from "@/utils/getIconSize";

export type CTAListElementType = Exclude<
  CTAListModuleType["cta_list"]["elements"],
  false
>[number];

export type CTAListLayoutOptionsType = {
  //   title_style: "normal" | "bold";
  icon_align_class: "is-vcentered" | "default";
  flex_direction_class: "is-flex-direction-row" | "is-flex-direction-column";
  row_direction_class: "is-row-normal-desktop" | "is-row-reverse-desktop";
  text_align_class: "has-text-centered" | "has-text-left";
};

type CTAListBoxProps = {
  CTAListElement: CTAListElementType;
  extraClass: string;
  ctaListOptions: CTAListModuleOptionsType;
  itemWidth?: Record<Breakpoint, string>;
  show_cardbox: boolean;
  show_divider: boolean;
  heading_level: number;
  layoutOptions: CTAListLayoutOptionsType;
  skeleton?: boolean;
  fallbackIconUrl?: { type: string; url: string };
};

export const CTAListBox = (props: CTAListBoxProps) => {
  const {
    CTAListElement: element,
    ctaListOptions,
    itemWidth,
    show_cardbox,
    show_divider,
    heading_level,
    layoutOptions,
    fallbackIconUrl,
    skeleton = false,
  } = props;
  const {
    flex_direction_class,
    icon_align_class,
    row_direction_class,
    // title_style,
    text_align_class,
  } = layoutOptions ?? {};

  //In Backend we can get both true at the same time, so show_cardbox has priority
  const effectiveShowCardbox = show_cardbox;
  const effectiveShowDivider = show_cardbox ? false : show_divider;

  const { show_extended_link, button_type, box, mobile_layout, show_icon } =
    ctaListOptions ?? {};
  const icon_size = ctaListOptions?.icon_size ?? null;
  const title = element?.title;
  const link = element?.link;
  const link_extended = element?.link_extended;
  const skeletonElementClass = skeleton ? "skeleton-element" : "";

  const { show_border_radius, show_box_shadow, box_padding } = box ?? {};

  const showBowClasses = getShowBoxClasses(
    effectiveShowCardbox,
    effectiveShowDivider,
    show_border_radius,
    show_box_shadow,
  );

  const attr = getItemAttributes({
    itemWidth: itemWidth,
    className: `${styles["article"]}`,
    itemAttrFor: "ctalist",
  });

  const { getLabel } = useGlobalContext();
  const standardButtonLink =
    !show_extended_link && link && typeof link === "object" && link.url
      ? link
      : null;
  const standardButtonType =
    button_type === "primary" ? "primary" : "secondary";
  const showTagline = skeleton || Boolean(element?.tagline);
  const showTitle =
    skeleton ||
    (title &&
      (button_type !== "link-in-title" ||
        !checkLinkAvailable(
          (show_extended_link ? link_extended : link) || false,
          show_extended_link,
        )));
  const showButton = Boolean(
    button_type !== "link-in-title" &&
    (skeleton || (title && standardButtonLink)),
  );

  // jscpd:ignore-start
  const TITLE_JSX = (
    <>
      {showTagline && (
        <span
          className={`cta-liste-overline is-block ${skeletonElementClass}`}
          dangerouslySetInnerHTML={{
            __html: customSanitize(
              skeleton ? "CTA list overline" : element?.tagline || "",
            ),
          }}
        />
      )}
      {showTitle && (
        <span
          role="heading"
          aria-level={heading_level}
          className={`cta-liste-title ${styles["title"]} is-block ${skeletonElementClass}`}
          dangerouslySetInnerHTML={{
            __html: customSanitize(skeleton ? "CTA list title" : title || ""),
          }}
        />
      )}
    </>
  );

  const IconJSX = show_icon ? (
    <>
      <div
        className={`cta-list-icon ${styles["cta-list-icon"]} ${skeletonElementClass}`}
      >
        {element?.svg && (
          <>
            {element.svg.mime_type === "image/svg+xml" && (
              <CustomSVG
                src={element.svg.url}
                wrapper="svg"
                className="is-flex"
                width={"100%"}
                height={"100%"}
                role="img"
                aria-label={`${element.svg.title} icon`}
              />
            )}
            {element.svg.mime_type !== "image/svg+xml" && (
              <Image
                src={element.svg.url}
                alt={element.svg.alt}
                sizes="150px"
                height={element.svg.height}
                width={element.svg.width}
                quality={90}
              />
            )}
          </>
        )}
        {!element?.svg && fallbackIconUrl && fallbackIconUrl.url && (
          <CustomSVG
            src={fallbackIconUrl.url}
            wrapper="svg"
            className="is-flex"
            width={"100%"}
            height={"100%"}
            role="img"
            aria-label={`${fallbackIconUrl.type} icon`}
          />
        )}
      </div>
    </>
  ) : (
    <></>
  );

  const ContentJSX = (
    <div className={`is-flex ${styles["cta-list-content"]}`}>
      <div className="h-fit">
        {TITLE_JSX}

        {title &&
          !show_extended_link &&
          link &&
          link.url &&
          button_type === "link-in-title" && (
            <Button
              role="button"
              className={`mb-0`}
              type={"secondary"}
              href={link.url}
              target={link.target === "" ? "_self" : link.target}
              aria-label={link.aria_label}
            >
              {link.title}
            </Button>
          )}

        {title &&
          button_type === "link-in-title" &&
          show_extended_link &&
          link_extended && (
            <ExtendedLink
              extendedLink={replaceExtendedLinkText(title, link_extended)}
              btnType={"secondary"}
              extraClass={`mt-0 cta-liste-title`}
              getLabel={getLabel}
            />
          )}

        {/* {element.content && (
          <ContentTag
            extraClass={`cta-list-text`}
            content={element.content ?? ""}
            disablePaddingBottom
          />
        )} */}
      </div>

      {showButton &&
        (skeleton ? (
          <span
            className={`${styles["skeleton-button"]} ${skeletonElementClass}`}
            aria-hidden={true}
          >
            Button
          </span>
        ) : standardButtonLink ? (
          <Button
            role="button"
            type={standardButtonType}
            href={standardButtonLink.url}
            target={
              standardButtonLink.target === ""
                ? "_self"
                : standardButtonLink.target
            }
            aria-label={standardButtonLink.aria_label}
            className={` mt-0 ${styles[button_type]}`}
          >
            <span className={styles["btn-text"]}>
              {standardButtonLink.title}
            </span>
          </Button>
        ) : null)}

      {show_extended_link &&
        link_extended &&
        button_type !== "link-in-title" && (
          <ExtendedLink
            extendedLink={link_extended}
            btnType={button_type}
            getLabel={getLabel}
          />
        )}
    </div>
  );
  // jscpd:ignore-end
  return (
    <article
      {...attr}
      aria-hidden={skeleton ? true : undefined}
      style={{
        ...attr.style,
        ...(box_padding !== undefined ? getBoxPadding(box_padding) : {}),
        ...(icon_size && (getIconSize(icon_size) as React.CSSProperties)),
      }}
    >
      <div
        data-flex-direction={flex_direction_class}
        data-icon-align={icon_align_class}
        // data-title-style={title_style}
        data-is-centered={
          text_align_class === "has-text-centered" &&
          flex_direction_class === "is-flex-direction-column"
            ? "true"
            : "false"
        }
        className={`${styles["cta-list-element"]} ${
          !effectiveShowCardbox && effectiveShowDivider
            ? styles["has-divider"]
            : ""
        } ${showBowClasses} ${styles[mobile_layout]}`}
      >
        {row_direction_class === "is-row-normal-desktop" && (
          <>
            {IconJSX}
            {ContentJSX}
          </>
        )}
        {row_direction_class === "is-row-reverse-desktop" && (
          <>
            {ContentJSX}
            {IconJSX}
          </>
        )}
      </div>
    </article>
  );
};
